<template>
    <div class="afp">
        <div class="afp-card">
            <div class="afp-header">
                <div class="afp-title">
                    <i class="pi pi-sliders-h"/>
                    <span>{{ $t('AppAnalysisFilterPanel.Filtros') }}</span>
                    <span v-if="activeCount > 0" class="afp-count">{{ activeCount }}</span>
                </div>
                <button type="button" class="afp-clear" :disabled="activeCount === 0" @click="clearFilters">
                    <i class="pi pi-filter-slash"/>
                    <span>{{ $t('AppAnalysisFilterPanel.Limpar filtros') }}</span>
                </button>
            </div>

            <div class="afp-grid">
                <div v-for="field in fields" :key="field.key" class="afp-field"
                     :class="{'afp-field--active': isFieldActive(field.key)}">
                    <label class="afp-label">
                        <span class="afp-dot"/>
                        {{ field.label }}
                    </label>
                    <MultiSelect :value="filters[field.key].selected" :options="options[field.key]"
                                 :filter="true" display="chip" appendTo="body"
                                 :placeholder="$t('AppAnalysisFilterPanel.Todos')"
                                 class="afp-select"
                                 @input="updateSelected(field.key, $event)"/>
                    <div class="afp-search">
                        <i class="pi pi-search afp-search-icon"/>
                        <input type="text" class="afp-search-input" :value="filters[field.key].text"
                               :placeholder="$t('AppAnalysisFilterPanel.Contém')"
                               @input="updateText(field.key, $event.target.value)"/>
                        <i v-if="filters[field.key].text" class="pi pi-times afp-search-clear"
                           @click="updateText(field.key, '')"/>
                    </div>
                </div>
            </div>

            <transition name="afp-fade">
                <div v-if="is_empty_result" class="afp-empty">
                    <i class="pi pi-info-circle"/>
                    <span>{{ $t('AppAnalysisFilterPanel.Nenhum evento com os filtros aplicados') }}</span>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import MultiSelect from 'primevue/multiselect';
import {countActiveFilters, createEmptyFilters, FILTER_FIELDS} from "./analysisFilters";

const FIELD_LABELS = {
    day: 'AppAnalysisSummary.Dia',
    vehicle_display_id: 'AppAnalysisSummary.Veículo',
    operator_id: 'AppAnalysisSummary.Motorista',
    field: 'AppAnalysisSummary.Local',
    type: 'AppAnalysisSummary.Condição',
    reason_description: 'AppAnalysisSummary.Motivo',
    shift: 'AppAnalysisSummary.Turno'
};

export default {
    model: {
        prop: 'filters',
        event: 'input'
    },
    props: {
        filters: {
            type: Object,
            required: true
        },
        options: {
            type: Object,
            required: true
        },
        is_empty_result: {
            type: Boolean,
            default: false
        }
    },
    computed: {
        fields() {
            return FILTER_FIELDS.map(key => ({key: key, label: this.$t(FIELD_LABELS[key])}));
        },
        activeCount() {
            return countActiveFilters(this.filters);
        }
    },
    methods: {
        isFieldActive(field) {
            let filter = this.filters[field];
            return filter.selected.length > 0 || filter.text.trim() !== '';
        },
        updateSelected(field, value) {
            let next = JSON.parse(JSON.stringify(this.filters));
            next[field].selected = value;
            this.$emit('input', next);
        },
        updateText(field, value) {
            let next = JSON.parse(JSON.stringify(this.filters));
            next[field].text = value;
            this.$emit('input', next);
        },
        clearFilters() {
            this.$emit('input', createEmptyFilters());
        }
    },
    components: {
        MultiSelect
    }
}
</script>

<style lang="scss" scoped>
$afp-primary: $APP_THEME_BTN_PRIMARY_BACKGROUND_COLOR;
$afp-on-primary: $APP_THEME_BTN_PRIMARY_TEXT_COLOR;
$afp-primary-soft: rgba($afp-primary, 0.1);
$afp-primary-ring: rgba($afp-primary, 0.22);
$afp-border: #e4e7ec;
$afp-text: #344054;
$afp-muted: #667085;
$afp-surface: #f9fafb;

.afp {
    padding: 12px 16px 4px 16px;
}

.afp-card {
    background: #ffffff;
    border: 1px solid $afp-border;
    border-radius: 14px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04), 0 4px 12px rgba(16, 24, 40, 0.06);
    padding: 16px 20px 20px 20px;
}

.afp-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
}

.afp-title {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 15px;
    font-weight: 600;
    color: $afp-text;

    .pi {
        color: $afp-primary;
        font-size: 16px;
    }
}

.afp-count {
    min-width: 22px;
    height: 22px;
    padding: 0 7px;
    border-radius: 999px;
    background: $afp-primary;
    color: $afp-on-primary;
    font-size: 12px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.afp-clear {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border: none;
    background: transparent;
    color: $afp-muted;
    font-size: 13px;
    font-weight: 500;
    padding: 6px 10px;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
        background: $afp-primary-soft;
        color: $afp-text;
    }

    &:disabled {
        opacity: 0.45;
        cursor: default;
    }
}

.afp-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(210px, 1fr));
    gap: 14px;
}

.afp-field {
    display: flex;
    flex-direction: column;
    gap: 6px;
    padding: 10px;
    border-radius: 10px;
    background: $afp-surface;
    border: 1px solid transparent;
    transition: border-color 0.2s ease, background-color 0.2s ease;
}

.afp-field--active {
    background: $afp-primary-soft;
    border-color: rgba($afp-primary, 0.35);

    .afp-label {
        color: $afp-text;
    }

    .afp-dot {
        opacity: 1;
        transform: scale(1);
    }
}

.afp-label {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $afp-muted;
    margin: 0;
}

.afp-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: $afp-primary;
    opacity: 0;
    transform: scale(0.4);
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.afp-select {
    width: 100%;
    border-radius: 8px;
    border-color: $afp-border;
    background: #ffffff;

    &:not(.p-disabled):hover {
        border-color: $afp-primary;
    }

    &:not(.p-disabled).p-focus {
        border-color: $afp-primary;
        box-shadow: 0 0 0 3px $afp-primary-ring;
    }

    ::v-deep .p-multiselect-label {
        padding: 6px 10px;
        font-size: 13px;
    }

    ::v-deep .p-multiselect-token {
        background: $afp-primary-soft;
        color: $afp-text;
        border-radius: 6px;
        padding: 1px 6px;
        font-size: 12px;
    }
}

.afp-search {
    position: relative;
    display: flex;
    align-items: center;
}

.afp-search-icon {
    position: absolute;
    left: 10px;
    font-size: 12px;
    color: $afp-muted;
    pointer-events: none;
}

.afp-search-input {
    width: 100%;
    height: 34px;
    padding: 0 28px 0 30px;
    font-size: 13px;
    color: $afp-text;
    background: #ffffff;
    border: 1px solid $afp-border;
    border-radius: 8px;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &::placeholder {
        color: #98a2b3;
    }

    &:hover {
        border-color: $afp-primary;
    }

    &:focus {
        border-color: $afp-primary;
        box-shadow: 0 0 0 3px $afp-primary-ring;
    }
}

.afp-search-clear {
    position: absolute;
    right: 9px;
    font-size: 11px;
    color: $afp-muted;
    cursor: pointer;
    padding: 3px;
    border-radius: 50%;

    &:hover {
        color: $afp-text;
        background: $afp-border;
    }
}

.afp-empty {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-top: 16px;
    padding: 10px 14px;
    border-radius: 10px;
    background: #fff8eb;
    border: 1px solid #fedf89;
    color: #93370d;
    font-size: 13px;
    font-weight: 500;
}

.afp-fade-enter-active,
.afp-fade-leave-active {
    transition: opacity 0.2s ease;
}

.afp-fade-enter,
.afp-fade-leave-to {
    opacity: 0;
}
</style>
